/**
 * Adverse Drug Reaction (ADR) Service
 * Manages adverse drug reaction records with localStorage persistence and audit logging.
 */

import LocalStorageService from '../storage/LocalStorageService';
import {
  createAdverseDrugReaction,
  validateAdverseDrugReaction,
  ADR_STATUS,
  ADR_SEVERITY,
} from '../../data/models/AdverseDrugReaction';

const LOG_PREFIX = '[ADRService]';

class AdverseDrugReactionService {
  constructor() {
    this.storageKey = 'agedcare_adverse_drug_reactions';
  }

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  async getAll() {
    try {
      const records = LocalStorageService.get(this.storageKey, []);
      console.debug(`${LOG_PREFIX} getAll → ${records.length} ADR records`);
      return { success: true, data: records };
    } catch (error) {
      console.error(`${LOG_PREFIX} getAll failed:`, error);
      return { success: false, error: error.message };
    }
  }

  async getByResident(residentId) {
    try {
      const records = LocalStorageService.find(
        this.storageKey,
        (r) => r.residentId === residentId,
      );
      return { success: true, data: records };
    } catch (error) {
      console.error(`${LOG_PREFIX} getByResident failed:`, error);
      return { success: false, error: error.message };
    }
  }

  async getById(id) {
    try {
      const record = LocalStorageService.findById(this.storageKey, id);
      if (!record) return { success: false, error: 'ADR record not found' };
      return { success: true, data: record };
    } catch (error) {
      console.error(`${LOG_PREFIX} getById failed:`, error);
      return { success: false, error: error.message };
    }
  }

  async getActive() {
    try {
      const records = LocalStorageService.find(
        this.storageKey,
        (r) => r.status === ADR_STATUS.ACTIVE || r.status === ADR_STATUS.MONITORING,
      );
      return { success: true, data: records };
    } catch (error) {
      console.error(`${LOG_PREFIX} getActive failed:`, error);
      return { success: false, error: error.message };
    }
  }

  async getSevere() {
    try {
      const records = LocalStorageService.find(
        this.storageKey,
        (r) => r.severity === ADR_SEVERITY.SEVERE || r.severity === ADR_SEVERITY.ANAPHYLAXIS,
      );
      return { success: true, data: records };
    } catch (error) {
      console.error(`${LOG_PREFIX} getSevere failed:`, error);
      return { success: false, error: error.message };
    }
  }

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  async create(adrData) {
    try {
      const adr = createAdverseDrugReaction(adrData);
      const validation = validateAdverseDrugReaction(adr);

      if (!validation.isValid) {
        console.warn(`${LOG_PREFIX} Validation failed:`, validation.errors);
        return { success: false, errors: validation.errors };
      }

      const created = LocalStorageService.addItem(this.storageKey, adr);

      LocalStorageService.logAudit({
        action: 'CREATE_ADR',
        action_type: 'CREATE',
        record_type: 'AdverseDrugReaction',
        record_id: created.id,
        new_value: created,
        user_id: adrData.reportedBy || 'system',
        metadata: {
          residentId: adr.residentId,
          drug: adr.drug,
          severity: adr.severity,
          reaction: adr.reaction,
        },
      });

      console.debug(`${LOG_PREFIX} ADR created: ${adr.drug} → ${adr.reaction} [${adr.severity}]`);
      return { success: true, data: created };
    } catch (error) {
      console.error(`${LOG_PREFIX} create failed:`, error);
      return { success: false, error: error.message };
    }
  }

  async update(id, updates) {
    try {
      const existing = LocalStorageService.findById(this.storageKey, id);
      if (!existing) return { success: false, error: 'ADR record not found' };

      const updated = LocalStorageService.updateItem(this.storageKey, id, updates);

      LocalStorageService.logAudit({
        action: 'UPDATE_ADR',
        action_type: 'UPDATE',
        record_type: 'AdverseDrugReaction',
        record_id: id,
        previous_value: existing,
        new_value: updated,
        user_id: updates.updatedBy || 'system',
      });

      console.debug(`${LOG_PREFIX} ADR updated: ${id}`);
      return { success: true, data: updated };
    } catch (error) {
      console.error(`${LOG_PREFIX} update failed:`, error);
      return { success: false, error: error.message };
    }
  }

  async resolve(id, resolvedBy) {
    return this.update(id, {
      status: ADR_STATUS.RESOLVED,
      resolvedDate: new Date().toISOString(),
      updatedBy: resolvedBy,
    });
  }

  async markMonitoring(id, followUpDate, notes, updatedBy) {
    return this.update(id, {
      status: ADR_STATUS.MONITORING,
      followUpDate,
      followUpNotes: notes,
      updatedBy,
    });
  }

  async recordTGASubmission(id, referenceNumber, submittedBy) {
    return this.update(id, {
      tgaBlueCardSubmitted: true,
      tgaReferenceNumber: referenceNumber,
      tgaSubmissionDate: new Date().toISOString(),
      updatedBy: submittedBy,
    });
  }

  // ---------------------------------------------------------------------------
  // Analytics
  // ---------------------------------------------------------------------------

  async getStats() {
    try {
      const all = LocalStorageService.get(this.storageKey, []);
      const stats = {
        total: all.length,
        active: all.filter((r) => r.status === ADR_STATUS.ACTIVE).length,
        monitoring: all.filter((r) => r.status === ADR_STATUS.MONITORING).length,
        resolved: all.filter((r) => r.status === ADR_STATUS.RESOLVED).length,
        bySeverity: {
          mild: all.filter((r) => r.severity === ADR_SEVERITY.MILD).length,
          moderate: all.filter((r) => r.severity === ADR_SEVERITY.MODERATE).length,
          severe: all.filter((r) => r.severity === ADR_SEVERITY.SEVERE).length,
          anaphylaxis: all.filter((r) => r.severity === ADR_SEVERITY.ANAPHYLAXIS).length,
        },
        tgaSubmitted: all.filter((r) => r.tgaBlueCardSubmitted).length,
        pendingFollowUp: all.filter((r) => r.followUpRequired && !r.resolvedDate).length,
      };
      return { success: true, data: stats };
    } catch (error) {
      console.error(`${LOG_PREFIX} getStats failed:`, error);
      return { success: false, error: error.message };
    }
  }
}

const adverseDrugReactionService = new AdverseDrugReactionService();
export default adverseDrugReactionService;
